import PropTypes from 'prop-types'
import { compose } from 'redux'
import { Helmet } from 'react-helmet'
import { ProductFiltersShape, SortParamsShape } from 'store/lib/prop-types'
import Image from 'components/image'
import Link from 'components/link'
import { track } from 'lib/analytics'
import { babylistUrl, storeBrandPagePath } from 'lib/urls'
import PlacementsInterstitialModal from 'components/modal/PlacementsInterstitialModal'
import { PLACEMENT_INTERSTITIAL_UNIT } from 'lib/placements/units'
import { useEffect, useState } from 'react'
import { parseFilterQueryParams } from 'store/lib'
import { useBrand, useBrandProducts } from '../../api/hooks'
import { withActiveSortParams, withActiveProductFilters } from '../../reducers'
import ProductGrid, { mapHeroesToProductCards } from '../product-grid'
import FrequentlyAskedQuestions from '../FrequentlyAskedQuestions/FrequentlyAskedQuestions'
import ShopByGuides from '../shop-by-guides'
import { filterEmptyFilters } from '../product-filters/helpers'
import FacetsBar from '../Filters/components/FacetsBar/FacetsBar'

const HeroProductsBase = ({
  brandId,
  location,
  sortParams,
  productFilters,
  brandName,
  guides,
}) => {
  const deprecatedFilters = filterEmptyFilters(productFilters)
  const hasDeprecatedFilters = Object.keys(deprecatedFilters).length > 0
  const displayNewFacetsAndFilters = !hasDeprecatedFilters
  const [selectedFilters, setSelectedFilters] = useState(() => {
    const initialFilters = location.search
      ? parseFilterQueryParams(location.search)
      : {}
    return initialFilters
  })
  const [filtersToApply, setFiltersToApply] = useState(() =>
    displayNewFacetsAndFilters
      ? parseFilterQueryParams(location.search)
      : deprecatedFilters
  )
  const sortOption = new URLSearchParams(location.search).get('sort')

  useEffect(() => {
    const newFilters = displayNewFacetsAndFilters
      ? parseFilterQueryParams(location.search)
      : deprecatedFilters
    setFiltersToApply(newFilters)
  }, [location.search, productFilters])

  const handleClearFilters = () => {
    setFiltersToApply({})
    router.push(location.pathname)
  }

  const { data, isLoading } = useBrandProducts(brandId, {
    filters: filtersToApply,
    inFacetsFeature: displayNewFacetsAndFilters,
    sort: sortOption,
    orderBy: {
      key: sortParams?.sort,
      direction: sortParams?.order,
    },
  })

  const productCards = isLoading
    ? null
    : data.heroProducts.map(mapHeroesToProductCards)

  const facets = isLoading ? null : data.facets

  if (!isLoading && data.heroProducts.length > 0) {
    track.storeBrandViewed({
      eventLocation: track.EventLocation.STORE_MINUS_BRAND,
      brand: brandName,
    })
  }

  return (
    <>
      {displayNewFacetsAndFilters && facets && (
        <>
          <FacetsBar
            facets={facets}
            facetsType="brand"
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            slug={brandId}
          />
        </>
      )}
      {productCards?.length === 0 && (
        <div className="well flex col paxl align-center">
          <div className="text-center text-muted pvxxl">
            <div
              className="mbm flex col align-center"
              style={{ maxWidth: 480 }}
            >
              <Image
                src="//images.babylist.com/image/upload/v1715118923/spilled_milk_asfyrb.svg"
                width={185}
              />
              <div className="mtl">
                Whoops! We couldn't find any matches that fit your current
                filters. Adjust your selections or{' '}
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <Link
                  className="text-underline link-info"
                  onClick={handleClearFilters}
                >
                  clear all filters
                </Link>{' '}
                to see all available products.
              </div>
            </div>
          </div>
        </div>
      )}
      <ProductGrid
        activeSlugSet={null}
        className="mtl"
        guides={guides}
        productCards={productCards}
      />
    </>
  )
}

HeroProductsBase.propTypes = {
  brandId: PropTypes.number.isRequired,
  location: PropTypes.object.isRequired,
  sortParams: PropTypes.shape(SortParamsShape),
  productFilters: PropTypes.shape(ProductFiltersShape),
  brandName: PropTypes.string.isRequired,
  guides: PropTypes.array,
}

HeroProductsBase.defaultProps = {
  sortParams: {},
  productFilters: {},
  guides: [],
}

const HeroProducts = compose(
  withActiveProductFilters(),
  withActiveSortParams()
)(HeroProductsBase)

export const BrandPage = ({ location, params: { brandId, slug } }) => {
  const { data, isLoading } = useBrand(brandId)

  if (!data && isLoading) {
    return null
  }

  const {
    title,
    description,
    logoUrl,
    meta,
    name,
    frequentlyAskedQuestions,
    guides,
  } = data || {}

  return (
    <>
      <Helmet
        meta={[
          {
            name: 'description',
            content: meta.seoDescription,
          },
        ]}
        title={meta.seoTitle}
      >
        <link
          href={babylistUrl(storeBrandPagePath(slug, brandId))}
          rel="canonical"
        />
      </Helmet>
      <div className="flex col align-center justify-center well paxl">
        <Image
          alt={title}
          className="circle-mask"
          lazyLoad={false}
          src={logoUrl}
          style={{ width: 180, height: 180, background: '#e6e6e6' }}
        />
        <h1>{title}</h1>
        {description && <h2 className="text-center h5">{description}</h2>}
      </div>
      <HeroProducts
        brandId={brandId}
        brandName={name}
        guides={guides}
        location={location}
      />
      <ShopByGuides guides={guides} />
      {frequentlyAskedQuestions && frequentlyAskedQuestions.length > 0 && (
        <div className="mtxl">
          <FrequentlyAskedQuestions
            headerVariant="secondary"
            questions={frequentlyAskedQuestions}
          />
        </div>
      )}
      <PlacementsInterstitialModal
        eventLocation={track.EventLocation.STORE_MINUS_BRAND}
        unitId={PLACEMENT_INTERSTITIAL_UNIT}
      />
    </>
  )
}

BrandPage.propTypes = {
  location: PropTypes.object.isRequired,
  params: PropTypes.shape({
    slug: PropTypes.string,
    brandId: PropTypes.string,
  }).isRequired, // react-router params
}
